import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Gamepad2,
  Clapperboard,
  School,
  Award,
  Target,
  Eye,
} from "lucide-react";
import { getAbout } from "../lib/api";

const PILLAR_ICONS = [Gamepad2, Clapperboard, School];

const FALLBACK = {
  company: "Arkweaver TFX – Guardians of Alkebulan",
  powered_by: "The Edutainment Box",
  summary:
    "The Edutainment Box is a global interactive media and edutainment enterprise built to transform how youth navigate the digital world.",
  founder: {
    name: "Joan L. Washington",
    credentials: [
      "Master of Education, Oakland University",
      "Master of Science in Technical Management, Embry-Riddle Aeronautical University",
      "Department of Homeland Security Citizen Academy",
      "Former U.S. Army civilian systems supply chain management",
    ],
  },
  vision:
    "To build a safer, smarter, and more empathetic digital world where youth are equipped to recognize online threats, protect their mental health, and prevent online crises before they happen.",
  mission:
    "To deliver premium, gamified edutainment and cinematic storytelling that empowers students in grades 6–12 to master digital ethics while providing school systems with tools to identify and support youth in crisis.",
  pillars: [
    {
      name: "Webjaggs",
      tagline:
        "The gamification pillar — powered by the Webjaggs Cyber-Safety Decision Engine (WCDE).",
      body: "",
    },
    {
      name: "Arkweaver: Guardians of Alkebulan",
      tagline: "The cinematic arm of the ecosystem.",
      body: "",
    },
    {
      name: "School Activations",
      tagline: "Real-world intervention where it matters most.",
      body: "",
    },
  ],
};

export default function AboutPage() {
  const [data, setData] = useState(FALLBACK);

  useEffect(() => {
    getAbout()
      .then(setData)
      .catch(() => setData(FALLBACK));
  }, []);

  return (
    <div data-testid="about-page" className="min-h-screen bg-[#FAFAFA] bg-grain pt-20 sm:pt-24 pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          data-testid="back-to-landing"
          className="inline-flex items-center gap-2 text-sm text-[#1A1414]/60 hover:text-[#E60000] transition-colors mb-4"
        >
          <ArrowLeft size={16} /> Back to the worlds
        </Link>

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
        >
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B0000] mb-2">
            About — {data.powered_by}
          </p>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-[#1A1414] tracking-tighter text-balance leading-[1.02]">
            We empower youth to learn{" "}
            <span className="text-[#E60000]">digital survival</span> through curiosity
            and imagination — not fear.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[#1A1414]/75 max-w-3xl leading-relaxed">
            {data.summary} Every day, young people face online threats from sexual
            predators and sextortion schemes to bullying, manipulation, and scams. We
            replace passive videos and outdated assemblies with high-energy, culturally
            rich, immersive experiences that meet students where they are.
          </p>
        </motion.div>

        {/* Mission X explainer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-10 p-6 sm:p-8 rounded-3xl bg-[#1A1414] text-white relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-grain opacity-15 pointer-events-none" />
          <div className="relative grid md:grid-cols-[auto_1fr] gap-5 items-start">
            <div className="font-display font-black text-[7rem] leading-none text-[#FF8080] -mt-3">
              X
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF8080] mb-2">
                Mission X
              </p>
              <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight leading-tight">
                The &ldquo;X&rdquo; represents hidden threats, unknown variables, evolving risks,
                and possible solutions in a constantly changing digital landscape.
              </h2>
              <p className="mt-3 text-white/75 leading-relaxed">
                It teaches students to stay alert, question what is{" "}
                <em>not</em> being said, recognize what may be misleading, and
                understand that online safety requires both awareness and action.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Vision + Mission */}
        <div className="mt-10 grid md:grid-cols-2 gap-5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-6 sm:p-8 rounded-3xl bg-white border-2 border-[#FF8080]/30 shadow-arc"
            data-testid="vision-card"
          >
            <Eye size={28} className="text-[#E60000] mb-2" />
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B0000] mb-2">
              Vision
            </p>
            <p className="text-[#1A1414] leading-relaxed">{data.vision}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-6 sm:p-8 rounded-3xl bg-white border-2 border-[#FF8080]/30 shadow-arc"
            data-testid="mission-card"
          >
            <Target size={28} className="text-[#E60000] mb-2" />
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B0000] mb-2">
              Mission
            </p>
            <p className="text-[#1A1414] leading-relaxed">{data.mission}</p>
          </motion.div>
        </div>

        {/* Pillars */}
        <div className="mt-12">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B0000] mb-2">
            Our Ecosystem
          </p>
          <h2 className="font-display font-black text-3xl sm:text-4xl text-[#1A1414] tracking-tight mb-6">
            Three pillars. One mission.
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {data.pillars?.map((p, i) => {
              const Icon = PILLAR_ICONS[i] || Gamepad2;
              return (
                <motion.div
                  key={p.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="relative p-6 rounded-3xl bg-white border-2 border-[#FF8080]/30 shadow-arc hover:-translate-y-1 transition-all"
                  data-testid={`pillar-${i}`}
                >
                  <span className="absolute top-4 right-4 font-display font-black text-5xl text-[#FF8080]/30">
                    0{i + 1}
                  </span>
                  <Icon size={28} className="text-[#E60000] mb-3" />
                  <h3 className="font-display font-black text-xl text-[#1A1414] leading-tight">
                    {p.name}
                  </h3>
                  <p className="mt-2 text-sm text-[#8B0000] font-bold">{p.tagline}</p>
                  {p.body && (
                    <p className="mt-3 text-sm text-[#1A1414]/75 leading-relaxed">
                      {p.body}
                    </p>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Founder */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-[#FF8080]/15 to-white border-2 border-[#FF8080]/30"
          data-testid="founder-card"
        >
          <Award size={28} className="text-[#E60000] mb-2" />
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B0000] mb-1">
            Founder & CEO
          </p>
          <h3 className="font-display font-black text-3xl text-[#1A1414] tracking-tight">
            {data.founder?.name}
          </h3>
          <p className="mt-2 text-[#1A1414]/75 max-w-3xl leading-relaxed">
            Blends higher-education expertise with military-grade systems thinking to
            protect youth. Supported by an international brain trust stretching to South
            Africa, building a globally scalable ecosystem that reflects the
            cross-cultural reality of the students we protect.
          </p>
          <ul className="mt-4 grid sm:grid-cols-2 gap-2">
            {data.founder?.credentials?.map((c) => (
              <li
                key={c}
                className="flex items-start gap-2 text-sm text-[#1A1414]/80"
              >
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#E60000] shrink-0" />
                {c}
              </li>
            ))}
          </ul>
        </motion.div>

        {/* CTA */}
        <div className="mt-12 flex flex-wrap gap-3 justify-center">
          <Link
            to="/gaming"
            data-testid="about-cta-gaming"
            className="px-6 py-3 rounded-full bg-[#E60000] text-white font-display font-bold shadow-arc hover:bg-[#8B0000] hover:-translate-y-0.5 transition-all"
          >
            Try a mission
          </Link>
          <Link
            to="/youtube"
            data-testid="about-cta-youtube"
            className="px-6 py-3 rounded-full bg-white text-[#E60000] border-2 border-[#E60000] font-display font-bold hover:bg-[#FF8080]/15 transition-all"
          >
            Watch the series
          </Link>
        </div>
      </div>
    </div>
  );
}
